import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

function getSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function getEffectiveTheme(preference) {
  if (preference === 'light' || preference === 'dark') return preference;
  return getSystemTheme();
}

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(() => {
    try {
      return localStorage.getItem('theme-preference') || null;
    } catch {
      return null;
    }
  });

  const theme = getEffectiveTheme(preference);
  const isSystem = preference === null;

  // Apply data-theme attribute
  useEffect(() => {
    const root = document.documentElement;
    if (preference) {
      root.setAttribute('data-theme', preference);
    } else {
      root.removeAttribute('data-theme');
    }
  }, [preference]);

  // Listen for system preference changes when following system
  useEffect(() => {
    if (preference !== null) return;

    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => {
      // Force re-render so `theme` updates
      setPreference(null);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  const setTheme = useCallback((value) => {
    // value: 'light', 'dark', or null (system)
    setPreference(value);
    try {
      if (value) {
        localStorage.setItem('theme-preference', value);
      } else {
        localStorage.removeItem('theme-preference');
      }
    } catch {
      // localStorage unavailable
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, preference, isSystem, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
